/**
 * MatterReaderPane — reading a document without leaving the matter.
 *
 * Opening a source used to dispatch `showSidePanel({ type: 'reader' })`, an
 * 85%-wide slide-over that covered the tab you opened it from: you could read
 * the document or see the list it came from, never both. On the surface the
 * reader is a PANE — dockable, resizable, and side by side with the canvas.
 *
 * It renders exactly the two components the side panel did, chosen by what
 * the caller passed: `ReaderComponent` for a matter source (a law, a case, an
 * uploaded file, with its metadata and relevance) and `CustomFileReader` for
 * a plain file blob.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import ReaderComponent from '../../shared/TailwindComponents/ReaderComponent';
import CustomFileReader from '../../shared/CustomFileReader';
import type { MatterReaderContent } from './MatterSurfaceContext';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface Props {
  content: MatterReaderContent | null;
  onClose: () => void;
}

const MatterReaderPane: React.FC<Props> = ({ content, onClose }) => {
  const { t } = useTranslation();

  if (!content) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="text-xs text-fg-muted text-center">
          {t('Open a document or source to read it here.')}
        </p>
      </div>
    );
  }

  if (content.loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 p-6">
        <Spinner size="sm" />
        <p className="text-xs text-fg-muted text-center truncate max-w-full">{content.title}</p>
      </div>
    );
  }

  if (content.kind === 'source') {
    return (
      <ReaderComponent
        citation={content.citation}
        matterId={content.matterId}
        sourceType={content.sourceType}
        userAccessLevel={content.userAccessLevel}
        onClose={onClose}
        onDelete={content.onDelete}
      />
    );
  }

  return (
    <CustomFileReader
      fileUrl={content.fileUrl}
      content={content.content}
      isPdf={content.isPdf}
      filename={content.title}
    />
  );
};

export default MatterReaderPane;
